import { useCallback } from "react";
import { useFormikContext } from "formik";
import { object, string } from "yup";
import { Button, Card, Flex, Select, Text } from "@jsq/ds/core";
import { Form, FormSelect, SubmitButton } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { useDataPackageSections } from "../hooks/useDataPackageSections";
import * as styles from "../styles_vanilla.css";
import { FormQuerySelectSection, type QueryPreviewContext } from "./FormQuerySelectSection";
import type { DataPackageOption } from "./GeneralSettingsStep";
import type { SupportedTemplateType } from "~/graphql/supergraphTypes";

export type UpdateSettingsFormValues = {
  dataPackage: string;
  query: string;
};

const validationSchema = object({
  dataPackage: string().required("Data package is required"),
  query: string().required("Query is required"),
});

type UpdateSettingsStepProps = {
  downloadUrl: string | null;
  initialValues: UpdateSettingsFormValues;
  onSubmit: (values: UpdateSettingsFormValues) => void;
  dataPackageOptions: DataPackageOption[];
  dataPackagesLoading: boolean;
  viewerArenaId?: number | null;
  arenaName: string;
  onPreviewFields: (context: QueryPreviewContext) => void;
  supportedTemplateType?: SupportedTemplateType | null;
};

const FormFooter = () => {
  const { values, errors } = useFormikContext<UpdateSettingsFormValues>();
  const hasErrors = Object.keys(errors).length > 0;
  const hasEmptyRequiredField = !values.dataPackage || !values.query;

  return (
    <Flex justifyContent="flex-end">
      <SubmitButton disabled={hasEmptyRequiredField || hasErrors}>Next</SubmitButton>
    </Flex>
  );
};

export const UpdateSettingsStep = ({
  downloadUrl,
  initialValues,
  onSubmit,
  dataPackageOptions,
  dataPackagesLoading,
  viewerArenaId,
  arenaName,
  onPreviewFields,
  supportedTemplateType,
}: UpdateSettingsStepProps) => {
  const dataPackageSections = useDataPackageSections(dataPackageOptions, arenaName);

  const handleDownload = useCallback(() => {
    if (downloadUrl) {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    }
  }, [downloadUrl]);

  return (
    <Flex flexDirection="column" gap={24} className={styles.updateSettingsWrapper}>
      <Card title="Download template">
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <Text variant="secondary-md">
            Download the template to make changes. You will be prompted to upload the revised
            file in the next step.
          </Text>
          <Button
            displayAsLink
            icon="download"
            onClick={handleDownload}
            disabled={!downloadUrl}
          >
            Download template
          </Button>
        </Flex>
      </Card>

      <Form<UpdateSettingsFormValues>
        initialValues={initialValues}
        enableReinitialize
        yupValidationSchema={validationSchema}
        onSubmit={onSubmit}
        submitOnlyChangedValues={false}
      >
        <Flex flexDirection="column" gap={24}>
          <Card title="Data settings">
            <Flex flexDirection="column" gap={SPACING.MEDIUM}>
              <Text variant="secondary-md">
                Ensure the selected data package and query are up to date.
              </Text>

              <FormSelect
                name="dataPackage"
                label="Data package"
                placeholder="Select a data package"
                full
                disabled={dataPackagesLoading}
              >
                {dataPackageSections.map((section) => (
                  <Select.Section key={section.name} name={section.name}>
                    {section.packages.map((pkg) => (
                      <Select.Option key={pkg.id} value={String(pkg.id)}>
                        {pkg.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                ))}
              </FormSelect>

              <FormQuerySelectSection
                dataPackageOptions={dataPackageOptions}
                viewerArenaId={viewerArenaId}
                onPreviewFields={onPreviewFields}
                supportedTemplateType={supportedTemplateType}
              />
            </Flex>
          </Card>

          <FormFooter />
        </Flex>
      </Form>
    </Flex>
  );
};
